/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Industries — Compact bearing cross-reference widget (client JS)
 * assets/bearing-xref-mini.js
 *
 * Reads `.ifx-mini` containers, fetches /api/search for each one's PN,
 * and renders up to N equivalents. Filters out the source PN itself, and
 * prefers showing one entry per brand to keep the list diverse.
 *
 * Uses window.IFX (interchange-utils.js, loaded first by the snippet) for
 * escaping + analytics; degrades gracefully if utils failed to load.
 * The loading state is the server-rendered `.ifx-mini__loading` row — CSS
 * renders it as a skeleton shimmer (static gray under reduced motion).
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  var U = window.IFX;
  if (!U) {
    try { console.error("[ifx-mini] interchange-utils.js missing — using degraded local fallbacks"); } catch (e) { /* noop */ }
    U = {
      escapeHtml: function (s) {
        return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
          return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
        });
      },
      track: function () { /* degraded: no analytics */ },
    };
  }

  const containers = document.querySelectorAll(".ifx-mini");
  containers.forEach(initMini);

  async function initMini(el) {
    const apiBase = el.dataset.apiBase || "";
    const pn = (el.dataset.pn || "").trim();
    const limit = Math.max(1, Math.min(20, parseInt(el.dataset.limit || "8", 10)));
    const list = el.querySelector(".ifx-mini__list");
    if (!pn || !list) return;

    U.track("ifx_mini_view", { pn: pn });

    // Skeleton shimmer while loading (CSS handles the visual; the text stays
    // in the DOM for the aria-live announcement).
    list.innerHTML = '<li class="ifx-mini__loading">Loading equivalents&hellip;</li>';

    try {
      const url = new URL(apiBase + "/api/search");
      url.searchParams.set("q", pn);
      url.searchParams.set("limit", String(limit * 3)); // overfetch for de-dup
      const r = await fetch(url.toString(), { credentials: "omit" });
      if (!r.ok) throw new Error("API " + r.status);
      const data = await r.json();

      const all = (data.primary ? [data.primary] : []).concat(data.equivalents || []);
      const sourceNorm = pn.toUpperCase().replace(/[\s\-/.]/g, "");
      // Filter: drop the exact PN we searched for, then dedup by brand
      const seenBrands = new Set();
      const others = [];
      for (const b of all) {
        const bnNorm = (b.partNumber || "").toUpperCase().replace(/[\s\-/.]/g, "");
        if (bnNorm === sourceNorm) continue;
        const brandKey = (b.brand || "").toLowerCase();
        if (seenBrands.has(brandKey)) continue;
        seenBrands.add(brandKey);
        others.push(b);
        if (others.length >= limit) break;
      }

      if (others.length === 0) {
        list.innerHTML = '<li class="ifx-mini__empty">No cross-references in our database yet for this part.</li>';
        return;
      }

      list.innerHTML = others.map((b) =>
        '<li class="ifx-mini__item">'
        + '<span class="ifx-mini__brand">' + U.escapeHtml((b.brand || "").toUpperCase()) + "</span>"
        + '<span class="ifx-mini__pn">' + U.escapeHtml(b.partNumber || "") + "</span>"
        + "</li>"
      ).join("");
    } catch (err) {
      console.error("[ifx-mini] load failed", err);
      list.innerHTML = '<li class="ifx-mini__empty">Could not load cross-references right now.</li>';
    }
  }
})();
